import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Search, Globe, Users, Sparkles, ArrowRight, Copy, Share2, Crown
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// v5.7.61 — A league is "OFFICIAL" when its scope is one of the
// auto-managed global scopes (Yearly, Quarterly, etc.) — i.e. it was
// bootstrapped by the platform, not created by a regular user. We
// also fall back to `league_type === 'global'` for legacy rows that
// pre-date the `scope` field.
const isOfficialLeague = (L) =>
  (L?.scope || '').startsWith('global_') || L?.league_type === 'global';

export default function LeagueDiscovery({ onJoin }) {
  const [leagues, setLeagues] = useState([]);
  const [search, setSearch] = useState('');
  const [matchAnywhere, setMatchAnywhere] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      api.get('/leagues/discover', {
        params: { search, limit: 20, match_mode: matchAnywhere ? 'contains' : 'prefix' },
      }).then(res => {
        // v5.7.61 — Sort OFFICIAL (platform-bootstrapped) leagues to
        // the top of Discover so users see the canonical Yearly/
        // Quarterly tournaments before any player-made public lobby.
        const rows = res.data.leagues || [];
        rows.sort((a, b) => {
          const oa = isOfficialLeague(a) ? 0 : 1;
          const ob = isOfficialLeague(b) ? 0 : 1;
          if (oa !== ob) return oa - ob;
          return (b.member_count || 0) - (a.member_count || 0);
        });
        setLeagues(rows);
        setLoading(false);
      }).catch(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, matchAnywhere]);

  const handleJoin = async (leagueId) => {
    // For public leagues, get invite code and join
    try {
      const shareRes = await api.get(`/leagues/${leagueId}/share`);
      const code = shareRes.data.invite_code;
      if (code) {
        const res = await api.post('/leagues/join', { invite_code: code });
        toast.success(`Joined ${res.data.league_name}!`);
        // v5.7.94 — Flip the row's `is_member` flag locally so the button
        // transforms to "Joined" immediately, without needing a refetch.
        // The next `/discover` call (search change or remount) will also
        // return is_member: true from the server now.
        setLeagues((prev) =>
          prev.map((l) =>
            l.id === leagueId
              ? { ...l, is_member: true, member_count: (l.member_count || 0) + 1 }
              : l,
          ),
        );
        if (onJoin) onJoin();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join');
    }
  };

  const handleShare = async (leagueId) => {
    try {
      const res = await api.get(`/leagues/${leagueId}/share`);
      const text = res.data.share_text || res.data.invite_code || '';
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Invite copied to clipboard!');
      } catch (clipErr) {
        // Clipboard API not available — show the text instead
        toast.info(`Invite code: ${res.data.invite_code || 'N/A'}`);
      }
    } catch (e) { toast.error('Failed to get share info'); }
  };

  return (
    <div data-testid="league-discovery">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Discover Leagues</h3>
      </div>

      <div className="relative mb-1.5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={matchAnywhere ? 'Search anywhere in league names…' : 'Search by start of name…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-28"
          data-testid="league-search-input"
        />
        <Button
          size="sm"
          variant={matchAnywhere ? 'default' : 'outline'}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 text-[10px] px-2"
          onClick={() => setMatchAnywhere((v) => !v)}
          title={matchAnywhere
            ? 'Matching anywhere in the name. Click for faster starts-with search.'
            : 'Matching the start of names. Click to match anywhere in the name.'}
          data-testid="league-search-mode-toggle"
        >
          {matchAnywhere ? 'Match anywhere' : 'Starts with'}
        </Button>
      </div>
      {search && !loading && leagues.length === 0 && !matchAnywhere && (
        <button
          type="button"
          className="text-[11px] text-primary hover:underline mb-2 ml-1"
          onClick={() => setMatchAnywhere(true)}
          data-testid="league-search-try-anywhere"
        >
          No leagues start with “{search}”. Try matching anywhere →
        </button>
      )}
      {!(search && !loading && leagues.length === 0 && !matchAnywhere) && <div className="mb-2" />}

      <ScrollArea className="h-[300px]">
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : (
          <div className="space-y-2">
            {leagues.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No leagues found</p>
            )}
            {leagues.map((league) => {
              const official = isOfficialLeague(league);
              return (
                <div
                  key={league.id}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                    official
                      ? 'bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-transparent border-amber-500/40'
                      : 'bg-card border-border/60'
                  }`}
                  data-testid={`discover-league-${league.id}`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    official ? 'bg-amber-500/20' : 'bg-primary/10'
                  }`}>
                    {official ? (
                      <Crown className="h-5 w-5 text-amber-500" />
                    ) : (
                      <Users className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-medium truncate">{league.name}</p>
                      {official && (
                        <Badge
                          className="bg-amber-500 hover:bg-amber-500 text-black text-[9px] px-1.5 py-0 h-4"
                          data-testid={`discover-official-badge-${league.id}`}
                        >
                          OFFICIAL
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {league.competing_count ?? 0} competing · {league.member_count}/{league.max_members} joined ·{' '}
                      {official ? 'Fantasy World Star tournament' : league.league_type}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm" variant="ghost" className="h-7"
                      onClick={() => handleShare(league.id)}
                    >
                      <Share2 className="h-3 w-3" />
                    </Button>
                    {league.league_type !== 'global' && (
                      league.is_member ? (
                        // v5.7.94 — User is already in this league. Show a
                        // disabled "Joined" pill instead of "Join" so the
                        // second click can't trigger a 409. The pill stays
                        // visible (rather than hiding the action entirely)
                        // so the user has clear feedback that the previous
                        // join actually landed.
                        <Button
                          size="sm" variant="outline" className="h-7 text-xs"
                          disabled
                          data-testid={`discover-joined-${league.id}`}
                        >
                          Joined
                        </Button>
                      ) : !league.is_full ? (
                        <Button
                          size="sm" className="h-7 text-xs"
                          onClick={() => handleJoin(league.id)}
                          data-testid={`discover-join-${league.id}`}
                        >
                          Join
                        </Button>
                      ) : null
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
